import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import en from '../locales/en.json';
import gr from '../locales/gr.json';
import de from '../locales/de.json';
import es from '../locales/es.json';
import fr from '../locales/fr.json';
import ContactModal from '../components/ContactModal';

const LanguageContext = createContext();

const locales = {
  en,
  gr,
  de,
  es,
  fr
};

export const LanguageProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const supportedLangs = ['en', 'gr', 'de', 'es', 'fr'];
  
  // Parse path lang slug
  const pathParts = location.pathname.split('/'); // e.g. ["", "gr", "terms-attendees"]
  const pathLang = pathParts[1] && supportedLangs.includes(pathParts[1].toLowerCase()) 
    ? pathParts[1].toLowerCase() 
    : null;

  // Parse query parameter option
  const langQueryParam = searchParams.get('lang');
  const queryLang = langQueryParam && supportedLangs.includes(langQueryParam.toLowerCase()) 
    ? langQueryParam.toLowerCase() 
    : null;

  // targetLang is query parameter if present (capturing the option), otherwise path lang slug, otherwise 'en'
  const currentLang = queryLang || pathLang || 'en';

  useEffect(() => {
    // Skip language prefix redirect for standalone terms & privacy pages
    const staticTermsRoutes = ['/terms-organizers', '/terms-attendees', '/privacy-policy', '/privacy'];
    if (staticTermsRoutes.includes(location.pathname)) {
      return;
    }

    // Determine inner path (strip the language slug if present)
    let innerPath = '/';
    if (pathLang) {
      const rest = pathParts.slice(2).filter(Boolean).join('/');
      innerPath = rest ? `/${rest}` : '/';
    } else {
      const rest = pathParts.filter(Boolean).join('/');
      innerPath = rest ? `/${rest}` : '/';
    }

    // Clean search params (remove the captured lang query param)
    const newParams = new URLSearchParams(location.search);
    newParams.delete('lang');
    const searchStr = newParams.toString();
    const searchSuffix = searchStr ? `?${searchStr}` : '';

    // Build the canonical path using the slug structure
    const canonicalPath = `/${currentLang}${innerPath === '/' ? '' : innerPath}${searchSuffix}${location.hash}`;

    // Verify if current URL matches canonical URL
    const currentURL = `${location.pathname}${location.search}${location.hash}`;
    if (currentURL !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [currentLang, pathLang, location.pathname, location.search, location.hash, navigate]);

  const changeLang = (newLang) => {
    const targetLang = supportedLangs.includes(newLang?.toLowerCase()) ? newLang.toLowerCase() : 'en';
    // When manually changing language via UI, we write directly to the new slug path:
    let innerPath = '/';
    if (pathLang) {
      const rest = pathParts.slice(2).filter(Boolean).join('/');
      innerPath = rest ? `/${rest}` : '/';
    } else {
      const rest = pathParts.filter(Boolean).join('/');
      innerPath = rest ? `/${rest}` : '/';
    }

    const newParams = new URLSearchParams(location.search);
    newParams.delete('lang');
    const searchStr = newParams.toString();
    const searchSuffix = searchStr ? `?${searchStr}` : '';

    navigate(`/${targetLang}${innerPath === '/' ? '' : innerPath}${searchSuffix}${location.hash}`);
  };

  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = locales[currentLang];
    
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        // Fallback to English
        let fallbackValue = locales['en'];
        for (const fk of keys) {
          if (fallbackValue && fallbackValue[fk] !== undefined) {
            fallbackValue = fallbackValue[fk];
          } else {
            fallbackValue = null;
            break;
          }
        }
        value = fallbackValue || key;
        break;
      }
    }
    
    if (typeof value === 'string') {
      let result = value;
      Object.keys(params).forEach(paramKey => {
        result = result.replace(`{${paramKey}}`, params[paramKey]);
      });
      return result;
    }
    
    return key;
  };

  return (
    <LanguageContext.Provider value={{ currentLang, changeLang, t, isContactModalOpen, setIsContactModalOpen }}>
      {children}
      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};